/**
 * EditAddressDialog — billing address (line1 + line2 + city +
 * pincode + state). Same fields the retired AddressCard exposed,
 * lifted into a public dialog so the cover cell + Edit dropdown
 * can trigger it.
 */
import * as React from "react";
import { Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@flint/ui";
import type { customers as contract } from "@flint/api-contract";
import type { z } from "zod";
import { EditDialog } from "./edit-dialog.tsx";
import { SectionField } from "./section-field.tsx";

type CustomerRow = z.infer<typeof contract.customerRow>;

const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Delhi",
  "Chandigarh",
  "Puducherry",
  "Jammu and Kashmir",
  "Ladakh",
];

export interface EditAddressDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  customer: CustomerRow;
  pending: boolean;
  serverError: string | null;
  onSave: (patch: {
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    pincode?: string;
    country?: string;
  }) => Promise<void>;
}

export function EditAddressDialog({
  open,
  onOpenChange,
  customer,
  pending,
  serverError,
  onSave,
}: EditAddressDialogProps) {
  const [line1, setLine1] = React.useState(customer.addressLine1 ?? "");
  const [line2, setLine2] = React.useState(customer.addressLine2 ?? "");
  const [city, setCity] = React.useState(customer.city ?? "");
  const [state, setState] = React.useState(customer.state ?? "");
  const [pincode, setPincode] = React.useState(customer.pincode ?? "");
  const [country, setCountry] = React.useState(customer.country ?? "");

  React.useEffect(() => {
    if (open) {
      setLine1(customer.addressLine1 ?? "");
      setLine2(customer.addressLine2 ?? "");
      setCity(customer.city ?? "");
      setState(customer.state ?? "");
      setPincode(customer.pincode ?? "");
      setCountry(customer.country ?? "");
    }
  }, [open, customer]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const patch: Parameters<EditAddressDialogProps["onSave"]>[0] = {};
    if (line1.trim() !== (customer.addressLine1 ?? "")) patch.addressLine1 = line1.trim();
    if (line2.trim() !== (customer.addressLine2 ?? "")) patch.addressLine2 = line2.trim();
    if (city.trim() !== (customer.city ?? "")) patch.city = city.trim();
    if (state !== (customer.state ?? "")) patch.state = state;
    if (pincode.trim() !== (customer.pincode ?? "")) patch.pincode = pincode.trim();
    if (country.trim() !== (customer.country ?? "")) patch.country = country.trim();
    if (Object.keys(patch).length === 0) {
      onOpenChange(false);
      return;
    }
    await onSave(patch);
  };

  return (
    <EditDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Edit address"
      description="Used on tax invoices and the e-invoice IRN payload. Matches the customer's GST-registered address."
      pending={pending}
      serverError={serverError}
      onSubmit={handleSubmit}
    >
      <SectionField label="Address line 1">
        <Input
          value={line1}
          onChange={(e) => setLine1(e.target.value)}
          placeholder="Plot 47, Andheri East"
          autoFocus
        />
      </SectionField>
      <SectionField label="Address line 2" optional="optional">
        <Input
          value={line2}
          onChange={(e) => setLine2(e.target.value)}
          placeholder="Industrial Estate"
        />
      </SectionField>
      <div className="grid grid-cols-2 gap-3">
        <SectionField label="City">
          <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Mumbai" />
        </SectionField>
        <SectionField label="PIN code">
          <Input
            value={pincode}
            onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="400069"
            inputMode="numeric"
            className="font-mono tabular-nums"
          />
        </SectionField>
      </div>
      <SectionField label="State">
        <Select {...(state ? { value: state } : {})} onValueChange={(v) => setState(v)}>
          <SelectTrigger>
            <SelectValue placeholder="Select state…" />
          </SelectTrigger>
          <SelectContent>
            {INDIAN_STATES.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </SectionField>
      <SectionField label="Country" optional="optional">
        <Input
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          placeholder="Leave blank for India · e.g. United Arab Emirates"
        />
      </SectionField>
    </EditDialog>
  );
}
